import type { IndexableType } from 'dexie';
import { NomadDB } from '~/db/NomadDB.js';
import type { CategorySchema } from '@/types/schema';

export const getCategoriesService = (db: NomadDB) => {
  const get = async (params: any): Promise<CategorySchema> => {
    const category = await db.categories.get(params);
    return category;
  };

  const getByContentTypesAndLb = async (
    contentTypes: string[],
    libraryBookId: string
  ): Promise<CategorySchema> => {
    const params = contentTypes.map((contentType) => [
      contentType,
      libraryBookId,
    ]);
    const results = await db.categories
      .where('[contentType+libraryBookId]')
      .anyOf(params)
      .toArray();
    const category = results.length > 0 ? results[0] : null;
    return category;
  };

  const findByContentTypesAndLb = async (
    contentTypes: string[],
    libraryBookId: string
  ): Promise<CategorySchema[]> => {
    const params = contentTypes.map((contentType) => [
      contentType,
      libraryBookId,
    ]);
    const results = await db.categories
      .where('[contentType+libraryBookId]')
      .anyOf(params)
      .toArray();

    return results;
  };

  const findByIds = async (ids: string[]): Promise<CategorySchema[]> => {
    return await db.categories.where('id').anyOf(ids).toArray();
  };

  const findByContentTypes = async (
    contentType: string[]
  ): Promise<CategorySchema[]> => {
    return await db.categories
      .where('contentType')
      .anyOf(contentType)
      .toArray();
  };

  const update = async (
    category: CategorySchema,
    params: any = null
  ): Promise<IndexableType> => {
    return await db.categories.update(
      [category.id, category.libraryBookId],
      params || category
    );
  };

  const findByIdsAndLibraryBookId = async (
    ids: string[],
    libraryBookId: string
  ): Promise<CategorySchema[]> => {
    if (libraryBookId) {
      const idsAndLbId = ids.map((id) => [id, libraryBookId]);
      return await db.categories
        .where(['id+libraryBookId'])
        .anyOf(idsAndLbId)
        .toArray();
    } else {
      return findByIds(ids);
    }
  };

  const findByDisciplineIdChapterIdParentIdAndSubChapter = async (
    disciplineId: string,
    chapterId: string,
    parentId: string,
    libraryBookId: string
  ): Promise<CategorySchema[]> => {
    const params = [
      disciplineId,
      chapterId,
      parentId,
      libraryBookId,
      'sub-chapter',
    ];
    return await db.categories
      .where(['disciplineId+chapterId+parentId+libraryBookId+contentType'])
      .anyOf([params])
      .toArray();
  };

  const findByIdsAndLibraryBookIds = async (
    idsAndLbIds
  ): Promise<CategorySchema[]> => {
    return await db.categories
      .where(['id+libraryBookId'])
      .anyOf(idsAndLbIds)
      .toArray();
  };

  const findBySlugsAndLibraryBookIds = async (
    slugsAndLbIds
  ): Promise<CategorySchema[]> => {
    return await db.categories
      .where(['slug+libraryBookId'])
      .anyOf(slugsAndLbIds)
      .toArray();
  };

  const findByContentTypeAndLibraryBookId = async (
    contentType: string,
    libraryBookId: string
  ): Promise<CategorySchema[]> => {
    const categories = await db.categories
      .where(['contentType+libraryBookId'])
      .anyOf([[contentType, libraryBookId]])
      .toArray();

    return categories;
  };

  const findBySlugs = async (slugs: string[]): Promise<CategorySchema[]> => {
    const categories = await db.categories.where('slug').anyOf(slugs).toArray();

    return categories;
  };

  const findByTitle = async (title: string): Promise<CategorySchema[]> => {
    const categories = await db.categories
      .where('title')
      .anyOf(title)
      .toArray();

    return categories;
  };

  return {
    get,
    getByContentTypesAndLb,
    findByIds,
    findByIdsAndLibraryBookId,
    findByIdsAndLibraryBookIds,
    findByContentTypeAndLibraryBookId,
    findBySlugs,
    findByTitle,
    findByDisciplineIdChapterIdParentIdAndSubChapter,
    update,
    findByContentTypesAndLb,
    findByContentTypes,
    findBySlugsAndLibraryBookIds,
  };
};
